import React from 'react';
import { X, Calendar, DollarSign, Globe, Trash2 } from 'lucide-react';
import BaseModal from '../common/BaseModal';
import { formatDate } from '../../utils/dateUtils';
import { useConfirmation } from '../../hooks/useConfirmation';
import { getAccountIcon, getAccountTypeLabel } from '../../constants/accountTypes';

const AccountDetailsModal = ({ isOpen, onClose, account, onAccountDeleted }) => {
  const { confirm, ConfirmationModal } = useConfirmation();

  if (!account) return null;

  const handleDelete = async () => {
    const confirmed = await confirm(
      'Delete Account',
      `Are you sure you want to delete "${account.name}"? This action cannot be undone. If this account has related transactions or holdings, it will be deactivated instead of deleted.`,
      'danger',
      'Delete Account',
      'Cancel'
    );

    if (!confirmed) return;

    try {
      await onAccountDeleted(account.id);
      onClose();
    } catch (error) {
      console.error('Error deleting account:', error);
      // Error handling could be improved with a toast notification
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: account.currency || 'USD'
    }).format(amount || 0);
  };

  // formatDate imported from utils/dateUtils
  const formatDateTimeLong = (dateString) => {
    if (!dateString) return 'Never';
    // Use formatDate for the date part, timestamps handled by containing component
    return formatDate(dateString, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const Icon = getAccountIcon(account.account_type);

  return (
    <>
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      showCloseButton={false}
    >
      <div className="flex items-center justify-between p-6 border-b">
        <div className="flex items-center">
          <Icon className="h-8 w-8 text-gray-400 mr-3" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{account.name}</h2>
            <p className="text-sm text-gray-500">{getAccountTypeLabel(account.account_type)}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Close modal"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

        <div className="p-6">
          {/* Balance Card */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Current Balance</p>
                <p className="text-3xl font-bold">{formatCurrency(account.balance)}</p>
              </div>
              <DollarSign className="h-12 w-12 text-green-200" />
            </div>
          </div>

          {/* Account Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">
                  Institution
                </label>
                <p className="mt-1 text-lg text-gray-900">{account.institution}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">
                  Account Number
                </label>
                <p className="mt-1 text-lg text-gray-900">
                  {account.account_number || 'Not provided'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">
                  Account Type
                </label>
                <p className="mt-1 text-lg text-gray-900">{getAccountTypeLabel(account.account_type)}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">
                  <Globe className="inline h-4 w-4 mr-1" />
                  Currency
                </label>
                <p className="mt-1 text-lg text-gray-900">{account.currency}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">
                  Sync Status
                </label>
                <div className="mt-1 flex items-center">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    account.sync_enabled
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {account.sync_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-500 uppercase tracking-wide">
                  Account Status
                </label>
                <div className="mt-1 flex items-center">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    account.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {account.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <label className="block text-gray-500 uppercase tracking-wide">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Created
                </label>
                <p className="mt-1 text-gray-900">{formatDate(account.created_at)}</p>
              </div>
              <div>
                <label className="block text-gray-500 uppercase tracking-wide">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Last Updated
                </label>
                <p className="mt-1 text-gray-900">{formatDate(account.updated_at)}</p>
              </div>
              <div>
                <label className="block text-gray-500 uppercase tracking-wide">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Last Sync
                </label>
                <p className="mt-1 text-gray-900">{formatDate(account.last_sync)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <button
            type="button"
            onClick={handleDelete}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <Trash2 className="h-4 w-4 inline mr-2" />
            Delete Account
          </button>

          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            Close
          </button>
        </div>
    </BaseModal>

    <ConfirmationModal />
    </>
  );
};

export default AccountDetailsModal;